import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { num } from '../lib/format.js';
import { ConfirmDialog, ErrorState, LoadingState, Panel } from '../components/ui.js';
import '../screens-2a.css';

/**
 * Po importu ze SmartEmailingu (D-326): rozbor překryvů seznamů, kdo nesmí dostat marketing, doporučená
 * sada segmentů hubu (nanečisto počty, ostře uložení), přepočet aktivity a přehled duplicit.
 * Nic se nezapisuje do SmartEmailingu.
 */
interface Analysis {
  totals: {
    mirror: number;
    distinctEmails: number;
    memberships: number;
    withAnyList: number;
    histogram: Record<string, number>;
  };
  exclusion: {
    blacklisted: number;
    hardbounced: number;
    unsubscribedAny: number;
    unsubscribedConsentLists: number;
    unsubscribedOnlyOneList: number;
    union: number;
    unionWithoutUnsubOfCampaignLists: number;
  };
  byType: Array<{ type: string; lists: number; memberships: number }>;
  overlap: {
    lists: Array<{ id: string; name: string }>;
    pairs: Array<{
      a: string;
      b: string;
      both: number;
      jaccard: number;
      aInB: boolean;
      bInA: boolean;
    }>;
  };
}
interface Rec {
  code: string;
  group: string;
  name: string;
  description: string;
  members: number;
  withConsent: number;
}

const TYPE_LABEL: Record<string, string> = {
  interest: 'zájem o závod/sérii',
  watchdog: 'hlídací pes',
  buyer: 'koupili / byli na závodě',
  customer_state: 'zákaznický stav (segment)',
  activity: 'aktivita',
  newsletter: 'zpravodaj',
  campaign: 'jednorázová kampaň',
  archive: 'archiv Ecomail',
  segment_copy: 'kopie segmentu',
  exclusion: 'výluka',
  trigger: 'spouštěč',
  test: 'test',
  unknown: 'neznámé',
};

export function SeRecommended({
  brand,
  canWrite,
}: {
  brand: string;
  canWrite: boolean;
}): JSX.Element {
  const client = useQueryClient();
  const [wantAnalysis, setWantAnalysis] = useState(false);
  const [wantRec, setWantRec] = useState(false);
  const [ask, setAsk] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const analysis = useQuery<Analysis>({
    queryKey: ['se-analysis', brand],
    queryFn: () => api.get(`/api/provider-import/list-analysis${qs({ brand })}`),
    enabled: wantAnalysis,
  });
  const rec = useQuery<{ items: Rec[] }>({
    queryKey: ['se-recommended', brand],
    queryFn: () => api.get(`/api/segments/recommended${qs({ brand })}`),
    enabled: wantRec,
  });
  const save = useMutation({
    mutationFn: () => api.post<{ saved: number }>('/api/segments/recommended', { brand }),
    onSuccess: (r) => {
      setNote(
        `Uloženo ${num(r.saved)} segmentů. Najdete je v Komunikace → Segmenty (kód začíná rec_).`,
      );
      void client.invalidateQueries({ queryKey: ['segments'] });
    },
  });
  const activity = useMutation({
    mutationFn: () => api.post<{ changed: number }>('/api/customers/activity-recompute'),
    onSuccess: (r) => setNote(`Aktivita přepočítána, změnilo se ${num(r.changed)} kontaktů.`),
  });
  const dup = useQuery<{ totals: { email: number; phone: number; namePostal: number } }>({
    queryKey: ['customers-duplicates'],
    queryFn: () => api.get('/api/customers/duplicates'),
    enabled: false,
  });
  const mode = useQuery<{ unsubscribe_scope_mode?: string }>({
    queryKey: ['unsub-scope-mode'],
    queryFn: async () => {
      const r = await api.get<{ values?: Record<string, unknown> }>('/api/settings');
      const v = r.values?.unsubscribe_scope_mode;
      return { unsubscribe_scope_mode: typeof v === 'string' ? v : 'by_list_type' };
    },
  });
  const setMode = useMutation({
    mutationFn: (v: string) => api.patch('/api/settings', { unsubscribe_scope_mode: v }),
    onSuccess: () => {
      setNote('Rozsah odhlášení uložen; projeví se při dalším importu a synchronizaci kontaktů.');
      void client.invalidateQueries({ queryKey: ['unsub-scope-mode'] });
    },
  });
  const names = new Map(analysis.data?.overlap.lists.map((l) => [l.id, l.name]));

  return (
    <Panel
      title="Rozbor seznamů a doporučené seznamy hubu"
      subtitle="Co se v seznamech SmartEmailingu překrývá, kdo nesmí dostat marketing a jaká sada segmentů je nahradí. Jen čtení ze SmartEmailingu."
    >
      <p className="note">
        Postup: 1. rozbor seznamů, 2. doporučené seznamy nanečisto (počty bez uložení), 3. teprve
        pak uložení. Segmenty jsou dynamické nad kontakty hubu, vždy bez odhlášených, blokovaných a
        tvrdých odskoků; sloupec „se souhlasem“ ukazuje, kolik z nich má v hubu souhlas s
        marketingem.
      </p>
      <div className="s2a-filters">
        <button type="button" className="btn" onClick={() => setWantAnalysis(true)}>
          Rozbor seznamů
        </button>
        <button type="button" className="btn" onClick={() => setWantRec(true)}>
          Doporučené seznamy: nanečisto
        </button>
        {canWrite ? (
          <>
            <button
              type="button"
              className="btn primary"
              disabled={!rec.data}
              onClick={() => setAsk(true)}
            >
              Vytvořit doporučené seznamy
            </button>
            <button type="button" className="btn" onClick={() => activity.mutate()}>
              Přepočítat aktivitu kontaktů
            </button>
          </>
        ) : null}
        <button type="button" className="btn" onClick={() => void dup.refetch()}>
          Spočítat duplicity
        </button>
      </div>
      <p className="note" data-testid="se-owner-rules">
        <b>Souhlas:</b> jako souhlas se smějí použít Ecomail štítky „-subscribed“ (zdroj souhlasu
        <code> ecomail_subscribed</code>, zapíná se volbou při importu). Webové opt-iny a zpravodaje
        zatím <b>nejsou</b> souhlas a čekají na potvrzení. „Ecomail all contacts“ a „Ecomail
        investigace“ nikdy. <b>Úkol:</b> po nahrání zákazníků z e-shopu (produkce nebo dev)
        vyhodnoťte překryv „Ecomail investigace“ se zákazníky (kolik adres se propojí) a teprve
        potom o ní rozhodněte. <b>Datum narození</b> se neukládá.
      </p>
      <div className="s2a-filters">
        <label className="field">
          <span>Rozsah odhlášení ze SmartEmailingu</span>
          <select
            value={mode.data?.unsubscribe_scope_mode ?? 'by_list_type'}
            disabled={!canWrite}
            onChange={(e) => setMode.mutate(e.target.value)}
          >
            <option value="by_list_type">
              Podle typu seznamu (doporučeno: z kampaně nebo závodu jen ten závod)
            </option>
            <option value="all">Přísně: odhlášení odkudkoli blokuje všechen marketing</option>
          </select>
        </label>
      </div>
      {note ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}
      {dup.data ? (
        <p className="note" data-testid="dup-totals">
          Skupin duplicit: stejná adresa {num(dup.data.totals.email)}; návrhy „možný duplikát“
          (stejný telefon {num(dup.data.totals.phone)}, stejné jméno a PSČ{' '}
          {num(dup.data.totals.namePostal)}). Návrhy hub nikdy neslučuje sám.
        </p>
      ) : null}
      {analysis.isFetching ? <LoadingState what="rozbor seznamů" /> : null}
      {analysis.isError ? (
        <ErrorState error={analysis.error} onRetry={() => void analysis.refetch()} />
      ) : null}
      {analysis.data ? (
        <div data-testid="se-analysis">
          <p>
            Řádků zrcadla {num(analysis.data.totals.mirror)}, unikátních adres{' '}
            {num(analysis.data.totals.distinctEmails)}, členství v seznamech celkem{' '}
            {num(analysis.data.totals.memberships)}, v aspoň jednom seznamu{' '}
            {num(analysis.data.totals.withAnyList)}. Počet seznamů na kontakt:{' '}
            {Object.entries(analysis.data.totals.histogram)
              .map(([k, v]) => `${k}: ${num(v)}`)
              .join(', ')}
            .
          </p>
          <p>
            <b>Nesmí dostat marketing:</b> blokovaných {num(analysis.data.exclusion.blacklisted)},
            tvrdý odskok {num(analysis.data.exclusion.hardbounced)}, odhlášených z libovolného
            seznamu {num(analysis.data.exclusion.unsubscribedAny)} (z toho jen z jednoho seznamu{' '}
            {num(analysis.data.exclusion.unsubscribedOnlyOneList)}), z toho ze souhlasových seznamů{' '}
            {num(analysis.data.exclusion.unsubscribedConsentLists)}. Celkem vyřazeno{' '}
            {num(analysis.data.exclusion.union)}; kdyby se nepočítalo odhlášení z jednorázových
            kampaní, {num(analysis.data.exclusion.unionWithoutUnsubOfCampaignLists)}.
          </p>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Typ seznamu</th>
                  <th className="n">Seznamů</th>
                  <th className="n">Členství</th>
                </tr>
              </thead>
              <tbody>
                {analysis.data.byType.map((t) => (
                  <tr key={t.type}>
                    <td>{TYPE_LABEL[t.type] ?? t.type}</td>
                    <td className="n">{num(t.lists)}</td>
                    <td className="n">{num(t.memberships)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="s2c-sub">Největší překryvy (Jaccard)</h3>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Seznam A</th>
                  <th>Seznam B</th>
                  <th className="n">Společných</th>
                  <th className="n">Jaccard</th>
                  <th>Podmnožina</th>
                </tr>
              </thead>
              <tbody>
                {analysis.data.overlap.pairs.slice(0, 15).map((p) => (
                  <tr key={`${p.a}-${p.b}`}>
                    <td>{names.get(p.a) ?? p.a}</td>
                    <td>{names.get(p.b) ?? p.b}</td>
                    <td className="n">{num(p.both)}</td>
                    <td className="n">{p.jaccard.toFixed(2)}</td>
                    <td>{p.aInB ? 'A je celý v B' : p.bInA ? 'B je celý v A' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {rec.isFetching ? <LoadingState what="doporučené seznamy" /> : null}
      {rec.isError ? <ErrorState error={rec.error} onRetry={() => void rec.refetch()} /> : null}
      {rec.data ? (
        <div className="scroll" data-testid="se-recommended">
          <table>
            <thead>
              <tr>
                <th>Segment</th>
                <th>Co to je</th>
                <th className="n">Kontaktů</th>
                <th className="n">se souhlasem</th>
              </tr>
            </thead>
            <tbody>
              {rec.data.items.map((s) => (
                <tr key={s.code}>
                  <td>{s.name}</td>
                  <td className="muted">{s.description}</td>
                  <td className="n">{num(s.members)}</td>
                  <td className="n">{num(s.withConsent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <ConfirmDialog
        open={ask}
        title="Vytvořit doporučené seznamy?"
        confirmLabel="Vytvořit"
        busy={save.isPending}
        onCancel={() => setAsk(false)}
        onConfirm={() => {
          save.mutate();
          setAsk(false);
        }}
      >
        Uloží se {rec.data ? num(rec.data.items.length) : ''} dynamických segmentů hubu (kód rec_…).
        Do SmartEmailingu se nic nepředává a nic se neodesílá; stejné kódy se přepíšou.
      </ConfirmDialog>
    </Panel>
  );
}
